import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import defaultAuthor from "../../images/LandingPageImage/Author_img.svg";
import useBlogs from "../../services/LandingPage/useBlogs";
import LandingCta from "./LandingCta";
import Footer from "./Footer";
import { ChevronLeft, ChevronRight } from "lucide-react";

const BlogSection = () => {
  const [index, setIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  const { blogs, fetchAllBlogs, loading, error } = useBlogs();

  const visibleCount = isMobile ? 1 : 4;

  const maxIndex = useMemo(
    () => Math.max(0, (blogs?.length || 0) - visibleCount),
    [blogs, visibleCount],
  );

  // ================= Responsive =================
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    handleResize();

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    fetchAllBlogs();
  }, [fetchAllBlogs]);

  // Keep index valid when screen size/data changes.
  useEffect(() => {
    setIndex((currentIndex) => Math.min(currentIndex, maxIndex));
  }, [maxIndex]);

  const handlePrevious = () => {
    setIndex((prev) => (prev <= 0 ? maxIndex : prev - 1));
  };

  const handleNext = () => {
    setIndex((prev) => (prev >= maxIndex ? 0 : prev + 1));
  };

  const visibleBlogs = (blogs || []).slice(index, index + visibleCount);

  return (
    <>
      <section className="bg-gray-50 py-12 mt-10">
        <div className="max-w-7xl mx-auto px-4">
          {/* Heading */}
          <div className="text-center mb-10">
            <h2 className="text-3xl font-semibold text-gray-800">
              Blog Updates
            </h2>

            <p className="text-gray-600 mt-2">
              Stay informed with our latest blog posts and join the wave of
              innovation.
            </p>
          </div>

          {/* Cards */}
          {loading ? (
            <div className="text-center py-10">Loading blogs...</div>
          ) : error ? (
            <div className="text-center py-10 text-red-500">{error}</div>
          ) : visibleBlogs.length === 0 ? (
            <div className="text-center py-10 text-gray-500">
              No blogs found.
            </div>
          ) : (
            <div className="grid md:grid-cols-4 grid-cols-1 gap-8">
              {visibleBlogs.map((card) => (
                <div
                  key={card.id}
                  className="bg-white rounded-2xl shadow-md overflow-hidden hover:shadow-xl transition flex flex-col"
                >
                  {/* Image */}
                  <Link
                    to={`/blogs/${card.post_slug}`}
                    className="block h-40 w-full overflow-hidden"
                  >
                    <img
                      src={card.image}
                      alt={card.imageAlt || card.title}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </Link>

                  {/* Content */}
                  <div className="p-6 flex flex-col flex-grow">
                    <div className="flex items-center gap-2 text-sm text-gray-500 mb-3">
                      <img
                        src={card.authorImage || defaultAuthor}
                        alt={card.author || "Author"}
                        className="w-5 h-5 rounded-full object-cover"
                        onError={(e) => {
                          e.currentTarget.onerror = null;
                          e.currentTarget.src = defaultAuthor;
                        }}
                      />
                      <span className="line-clamp-1">{card.author}</span>
                    </div>

                    <h3 className="text-lg font-semibold mb-3 line-clamp-2">
                      <Link
                        to={`/blogs/${card.post_slug}`}
                        className="hover:text-green-600 transition"
                      >
                        {card.title}
                      </Link>
                    </h3>

                    <p className="text-gray-600 text-sm line-clamp-3">
                      {card.description}
                    </p>

                    <Link
                      to={`/blogs/${card.post_slug}`}
                      className="text-green-600 font-medium mt-auto pt-4"
                    >
                      Read more →
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Navigation Arrows */}
          {!loading && !error && blogs.length > visibleCount && (
            <div className="flex justify-center gap-10 mt-8">
              <button
                type="button"
                onClick={handlePrevious}
                aria-label="Previous blogs"
                className="cursor-pointer hover:opacity-70 transition-opacity"
              >
                <ChevronLeft
                  className="w-8 h-8 text-gray-700"
                  strokeWidth={2.5}
                />
              </button>

              <button
                type="button"
                onClick={handleNext}
                aria-label="Next blogs"
                className="cursor-pointer hover:opacity-70 transition-opacity"
              >
                <ChevronRight
                  className="w-8 h-8 text-gray-700"
                  strokeWidth={2.5}
                />
              </button>
            </div>
          )}
        </div>
      </section>

      <LandingCta />
      <Footer />
    </>
  );
};

export default BlogSection;
